import React from 'react'
import { Form, Button } from 'react-bootstrap'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { QrCode } from '@/components/ui/QrCode'
import { Box, LogoBox, ContentInput, MfaQrBox, MfaSecret } from './styles'
import { useIsMounted } from './useIsMounted'

interface CodeValues {
  code: string
}

const codeSchema = Yup.object({
  code: Yup.string()
    .matches(/^\d{6}$/, 'O código tem 6 dígitos')
    .required('Código obrigatório')
})

interface MfaSetupStepProps {
  /** `otpauth://totp/...` vinda do `POST /sessions`. Nunca é persistida. */
  otpauthUri: string
  /** O mesmo segredo em blocos de 4, já formatado pela API. */
  secretManual: string
  /** Envia o código. O erro é tratado por quem passou esta função. */
  onSubmit: (code: string) => Promise<void>
  /** Volta ao passo de e-mail/senha, descartando o challenge. */
  onBack: () => void
  /** Penalidade de 429 em curso: o botão fica fechado. */
  blocked?: boolean
  /** Aviso do contador regressivo do 429, renderizado pela página. */
  throttleNotice?: React.ReactNode
  /** Atribuição do reCAPTCHA, renderizada pela página. */
  attribution?: React.ReactNode
}

/**
 * Vinculação do app autenticador — primeiro acesso, ou depois de um admin
 * resetar o autenticador do usuário.
 *
 * Sem modal e sem aviso vermelho: a obrigatoriedade é ESTRUTURAL, não visual.
 * Não há sessão emitida neste ponto, recarregar a página devolve ao login e
 * "Voltar" significa recomeçar do e-mail. Não existe estado em que o usuário
 * esteja dentro da plataforma sem o autenticador vinculado, então não há o que
 * alertar.
 *
 * O segredo aparece em texto ao lado do QR de propósito: é a única saída para
 * quem está num desktop sem câmera disponível, e ele já está codificado dentro
 * do QR logo acima — escondê-lo não protegeria nada.
 */
export function MfaSetupStep({
  otpauthUri,
  secretManual,
  onSubmit,
  onBack,
  blocked = false,
  throttleNotice = null,
  attribution = null
}: MfaSetupStepProps): JSX.Element {
  const isMounted = useIsMounted()

  /*
   * Condicional para não renderizar uma caixa tracejada com um `<code>` vazio
   * se `secret_manual` faltar na resposta. É o mesmo princípio do guarda de
   * `otpauth_uri` na `AuthPage`: nenhuma moldura vazia na tela. O QR acima
   * continua sendo caminho de vinculação suficiente.
   */
  const manualSecret = secretManual ? (
    <MfaSecret>
      <span className="mfa-secret-label">Ou digite este código no app</span>
      <code>{secretManual}</code>
    </MfaSecret>
  ) : null

  return (
    <Formik
      initialValues={{ code: '' }}
      validationSchema={codeSchema}
      onSubmit={async (
        values: CodeValues,
        { setSubmitting }: FormikHelpers<CodeValues>
      ) => {
        try {
          await onSubmit(values.code)
        } catch (error) {
          // A mensagem sai no interceptor e a decisão de voltar ao passo
          // anterior é da página; aqui só se libera o botão no `finally`.
        } finally {
          // Só faz sentido se o passo continuar na tela; ver useIsMounted.
          if (isMounted()) {
            setSubmitting(false)
          }
        }
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        isSubmitting,
        errors,
        touched
      }) => (
        <Box onSubmit={handleSubmit}>
          <div>
            <LogoBox>Vincular autenticador</LogoBox>
            <ContentInput>
              <MfaQrBox>
                <QrCode
                  value={otpauthUri}
                  alt="QR Code para vincular o aplicativo autenticador"
                  /*
                   * Sem QR, o segredo manual ocupa o lugar dele. Nunca um
                   * quadrado em branco: a vinculação continua possível.
                   */
                  fallback={manualSecret}
                />
              </MfaQrBox>

              <Form.Text className="text-muted text-center">
                Escaneie o QR Code com o Google Authenticator, o Microsoft
                Authenticator ou outro app TOTP. É obrigatório vincular um app
                autenticador para acessar a plataforma.
              </Form.Text>

              {manualSecret}

              <Form.Group className="mb-0">
                <Form.Label>Código de 6 dígitos</Form.Label>
                <Form.Control
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.code}
                  className="input-radius"
                  isInvalid={!!(errors.code && touched.code)}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.code && touched.code && errors.code}
                </Form.Control.Feedback>
              </Form.Group>

              {throttleNotice}

              <Button
                disabled={isSubmitting || blocked}
                type="submit"
                className="d-block mt-3"
                style={{ width: '100%' }}
                variant="primary"
                block
              >
                Ativar e entrar
              </Button>
              <Button
                type="button"
                className="mt-2"
                style={{ width: '100%' }}
                variant="link"
                onClick={onBack}
              >
                Voltar
              </Button>
              {attribution}
            </ContentInput>
          </div>
        </Box>
      )}
    </Formik>
  )
}
